import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./PreLaunchGuard.css";

// Referral codes are captured by ReferralTracker, which renders OUTSIDE this
// guard and reads ?ref= before any redirect below runs. This component used to
// write axoix_ref itself, on every visit even with pre-launch mode off. That
// bypassed the EU/UK consent rule, and now that ReferralTracker waits for the
// region before storing, a cookie written here first would stop it reporting
// the click to the partner API at all.

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.axoix.in";

// Pre-launch configuration — key is validated server-side, never in the bundle
const PRE_LAUNCH_CONFIG = {
  enabled: process.env.REACT_APP_PRE_LAUNCH_MODE === "true",
  storageKey: "axoix_prelaunch_access",
  viewablePages: ["/", "/home"],
};

interface PreLaunchGuardProps {
  children: React.ReactNode;
}

const PreLaunchGuard: React.FC<PreLaunchGuardProps> = ({ children }) => {
  const [isUnlocked, setIsUnlocked] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [inputKey, setInputKey] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [attemptedPath, setAttemptedPath] = useState<string>("");
  const location = useLocation();
  const navigate = useNavigate();

  // Check if pre-launch mode is enabled
  const isEnabled = PRE_LAUNCH_CONFIG.enabled;

  // Check if current page is viewable without key
  const isViewablePage = PRE_LAUNCH_CONFIG.viewablePages.includes(location.pathname);

  // Check localStorage for existing access
  useEffect(() => {
    if (!isEnabled) {
      setIsUnlocked(true);
      return;
    }

    const storedToken = localStorage.getItem(PRE_LAUNCH_CONFIG.storageKey);
    if (storedToken) {
      setIsUnlocked(true);
    }
  }, [isEnabled]);

  // Intercept navigation for non-viewable pages
  useEffect(() => {
    if (!isEnabled || isUnlocked) return;

    if (!isViewablePage) {
      // Redirect to home and show modal
      setAttemptedPath(location.pathname);
      setShowModal(true);
      navigate("/", { replace: true });
    }
  }, [location.pathname, location.search, isEnabled, isUnlocked, isViewablePage, navigate]);

  // Handle key submission — validate server-side
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/api/v1/hyperion/prelaunch/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: inputKey.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem(PRE_LAUNCH_CONFIG.storageKey, data.token || "granted");
        setIsUnlocked(true);
        setShowModal(false);
        setError("");
        if (attemptedPath) {
          navigate(attemptedPath);
          setAttemptedPath("");
        }
      } else {
        setError("Invalid access key. Please try again.");
        setInputKey("");
      }
    } catch {
      setError("Unable to verify. Please try again.");
      setInputKey("");
    }
  }, [inputKey, attemptedPath, navigate]);

  // Handle overlay click
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  }, []);

  // Handle link/button clicks through event delegation
  useEffect(() => {
    if (!isEnabled || isUnlocked || !isViewablePage) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Don't intercept clicks inside the modal
      if (target.closest(".prelaunch-modal, .prelaunch-modal-overlay")) {
        return;
      }

      // Check if clicked element is a link or button or inside one
      const clickable = target.closest("a, button, [role='button'], .thm-btn");

      if (clickable) {
        e.preventDefault();
        e.stopPropagation();
        setShowModal(true);
      }
    };

    // Capture phase to intercept before other handlers
    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [isEnabled, isUnlocked, isViewablePage]);

  // If pre-launch mode is disabled or user is unlocked, render children normally
  if (!isEnabled || isUnlocked) {
    return <>{children}</>;
  }

  return (
    <div className="prelaunch-container">
      {children}

      {/* Overlay for viewable pages */}
      {isViewablePage && (
        <div
          className="prelaunch-overlay"
          onClick={handleOverlayClick}
        >
          <div className="prelaunch-badge" onClick={(e) => { e.stopPropagation(); setShowModal(true); }}>
            <span className="prelaunch-badge-icon">🚀</span>
            <span className="prelaunch-badge-text">Coming Soon</span>
            <span className="prelaunch-badge-subtext">Click anywhere to enter access key</span>
          </div>
        </div>
      )}

      {/* Access Key Modal */}
      {showModal && (
        <div className="prelaunch-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="prelaunch-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="prelaunch-modal-close"
              onClick={() => setShowModal(false)}
              aria-label="Close"
            >
              ×
            </button>

            <div className="prelaunch-modal-header">
              <div className="prelaunch-logo">
                <span className="prelaunch-logo-icon">🔐</span>
              </div>
              <h2 className="prelaunch-modal-title">Pre-Launch Access</h2>
              <p className="prelaunch-modal-subtitle">
                AXOIX is currently in pre-launch mode. Enter your access key to explore the full experience.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="prelaunch-form">
              <div className="prelaunch-input-group">
                <input
                  type="text"
                  value={inputKey}
                  onChange={(e) => { setInputKey(e.target.value); setError(""); }}
                  placeholder="Enter access key"
                  className={`prelaunch-input ${error ? "prelaunch-input-error" : ""}`}
                  autoFocus
                  autoComplete="off"
                />
                {error && <span className="prelaunch-error">{error}</span>}
              </div>

              <button type="submit" className="prelaunch-submit">
                <span>Unlock Access</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            </form>

            <div className="prelaunch-modal-footer">
              <p>Don't have an access key?</p>
              <a
                href="mailto:hello@axoix.com?subject=Pre-Launch Access Request"
                className="prelaunch-request-link"
              >
                Request Early Access
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreLaunchGuard;
